import { useCallback, useState } from "react";
import type { ReactElement } from "react";
import type { IncomingInvoice } from "@kundeninfo/api-client";
import { Hinweis } from "@kundeninfo/ui/me-design";
import { sharedAssignmentLists } from "../../KontierungFelder.js";
import { EingangsrechnungObjekt, useIncomingActions } from "../../../views/incomingInvoices/EingangsrechnungObjekt.js";
import { INCOMING_TEXT_WEB as T } from "../../../views/incomingInvoices/texts.js";
import { ObjektArbeitsdialog } from "../ObjektArbeitsdialog.js";
import type { InhaltProps } from "../ObjektPanelGastgeber.js";
import { ladeZustand, ticketWegAus, useObjektLaden } from "./lieferantenInhalt.js";

/**
 * DIE EINGANGSRECHNUNG IM OBJEKTPANEL (Spec F6; Plan Aufgabe 3): dasselbe `EingangsrechnungObjekt` wie in Akte und
 * Einkauf, aufgeklappt. Bearbeiten, Zahlung, Storno und Ticket stapeln darüber (`useIncomingActions`); nach jedem
 * Schreibweg lädt die Rechnung neu und die Ebene gilt als geändert (F9). Sprung links unten (F11), Ordner über
 * `sprungZiel` durch den Ausgang (PF3).
 */
export function EingangsrechnungInhalt(p: InhaltProps<"eingangsrechnung">): ReactElement {
  const { bezug, client, onGeaendert } = p;
  const [lists] = useState(() => sharedAssignmentLists(client));
  const [offen, setOffen] = useState(true);
  const laden = useCallback(() => client.getIncomingInvoice(bezug.customerId, bezug.id), [client, bezug.customerId, bezug.id]);
  const { stand, neu } = useObjektLaden<IncomingInvoice>(laden);
  const aktionen = useIncomingActions({ client, lists, companies: p.companies, ...ticketWegAus(p),
    onDone: () => { onGeaendert(); neu(); } });
  const zustand = ladeZustand(stand, T);
  // Die Panels der Aktionen neben dem Objektpanel, nicht darin: sie stapeln über ihm (F5).
  return (
    <>
      <ObjektArbeitsdialog titel={bezug.label} seitlich={p.seitlich} sprung={p.sprung} zustand={zustand}
        onAbbrechen={p.onSchliessen}
        {...(aktionen.fehler === null ? {} : { meldungen: <Hinweis art="fehler">{aktionen.fehler}</Hinweis> })}>
        {stand !== null && !(stand instanceof Error) && (
          <EingangsrechnungObjekt rechnung={stand} ort={p.akteKunde === bezug.customerId ? "akte" : "panel"} aufgeklappt={offen} onAufgeklappt={setOffen} client={client}
            lists={lists} companies={p.companies} mehrereUnternehmen={stand.companyId !== p.sitzungsUnternehmen} mitTicket={aktionen.mitTicket}
            onAktion={(a) => aktionen.start(a, stand)} onDateienGeaendert={onGeaendert} />
        )}
      </ObjektArbeitsdialog>
      {aktionen.panels}
    </>
  );
}
